import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { getOverdueAmount, getUpcomingAmount, } from '@/helpers/CartHelpers';
import { currencySymbol, formatCurrency } from '@/helpers/Currency';
import { clamp } from '@/helpers/Math';
import Price from '@/components/elements/Price.vue';
import { apiEntities } from '@/api/Helpers';
import store from '@/store/store';
@Component({
    name: 'ScheduledPayments',
    components: { Price },
})
export default class default_1 extends Vue {
    @Prop()
    partialPaymentResponse;
    selectedPartialPaymentType = 'due';
    showAllPayments = false;
    // Set by `anotherAmount` setter
    inputAmount = '0';
    partialPaymentOptions = [
        { name: 'due', label: 'Pay the amount due' },
        { name: 'outstanding', label: 'Pay the outstanding balance on the cart' },
        { name: 'other', label: 'Pay another amount' },
    ];
    created() {
        this.inputAmount = this.dueAmount.toFixed(2);
    }
    get showOnlyOutstandingOption() {
        return this.dueAmount === this.outstandingBalance;
    }
    get displayPartialPaymentOptions() {
        return this.partialPaymentOptions.filter((option) => {
            return this.showOnlyOutstandingOption ? option.name === 'outstanding' : true;
        });
    }
    set anotherAmount(v) {
        // There are `min` and `max` attributes specified on the form input.
        // However, they only work when using the input's steppers, so we need the following code
        // to restrict the input amount to be in range when a user chooses to type.
        this.inputAmount = clamp(v, this.dueAmount, this.outstandingBalance).toFixed(2);
    }
    get anotherAmount() {
        return this.inputAmount;
    }
    get paymentAmount() {
        if (!this.hasScheduledPayments) {
            return this.outstandingBalance;
        }
        switch (this.selectedPartialPaymentType) {
            case 'outstanding':
                return this.outstandingBalance;
            case 'other':
                return Number(this.anotherAmount);
            default:
                return this.dueAmount;
        }
    }
    @Watch('paymentAmount', { immediate: true })
    updateCartPartialPaymentAmount(amount) {
        store.commit('Cart/partialPaymentAmount', amount);
    }
    get hasScheduledPayments() {
        return store.getters['Cart/hasScheduledPayments'];
    }
    get scheduledPayments() {
        return store.getters['Cart/scheduledPayments'];
    }
    get completedPayments() {
        return store.getters['Cart/completedPayments'];
    }
    /**
     * Reset payment option after a payment has been made
     */
    @Watch('completedPayments', { immediate: true })
    handleCompletedPaymentsChange() {
        this.selectedPartialPaymentType = this.showOnlyOutstandingOption ? 'outstanding' : 'due';
    }
    get dueAmount() {
        return getUpcomingAmount(this.scheduledPayments) + this.overdueAmount;
    }
    get overdueAmount() {
        return getOverdueAmount(this.scheduledPayments);
    }
    get dueDate() {
        return this.scheduledPayments[0]?.dueDate;
    }
    get outstandingBalance() {
        return store.getters['Cart/outstandingBalance'];
    }
    get paymentDueByCreditCard() {
        return store.getters['Cart/paymentDueByCreditCard'];
    }
    get partialPaymentSuccessMessage() {
        // There should only be success message when there's a partial payment response
        if (!this.partialPaymentResponse) {
            return null;
        }
        const { gateway_audit: payments } = apiEntities(this.partialPaymentResponse);
        const lastPayment = payments[payments.length - 1];
        let msg = `You have successfully made a payment of ${formatCurrency(Number(lastPayment.amount))}.`;
        if (this.dueAmount) {
            msg += ` Your next payment of ${formatCurrency(this.dueAmount)} is due on ${this.dueDate}.`;
        }
        return msg;
    }
    get displayScheduledPayments() {
        return this.scheduledPayments.map(({ id, dueDate, amount, status }) => ({
            id,
            date: dueDate,
            amount,
            status,
            class: status.toLowerCase(),
        }));
    }
    get displayCompletedPayments() {
        return this.completedPayments.map(({ id, occurredDate, amount, status }) => ({
            id,
            date: occurredDate,
            amount,
            status,
            class: status.toLowerCase(),
        }));
    }
    get paymentLists() {
        return [
            {
                title: 'Completed payments',
                class: 'completed-payments',
                items: this.displayCompletedPayments,
            },
            {
                title: 'Scheduled payments',
                class: 'scheduled-payments',
                items: this.displayScheduledPayments,
            },
        ];
    }
    get currencySymbol() {
        return currencySymbol();
    }
}
